import { supabase } from '@/lib/supabase';
import { uploadFile } from '@/lib/uploadFile';
import { getProxiedImageUrl } from '@/services/imageProxyService';
import { PostgrestFilterBuilder } from '@supabase/postgrest-js';
import { ClothingItem, ClothingItemFormData, ShoeItem, ShoeItemFormData } from '@/models/types';
import { normalizeFindSubcategory } from '@/lib/findSubcategories';

// Unified type for all products
export type ProductItem = ClothingItem | ShoeItem;
export type ProductItemFormData = ClothingItemFormData | ShoeItemFormData;

export interface FilterOptions {
  category?: string;
  brand?: string;
  findSubcategory?: string;
  minPrice?: number;
  maxPrice?: number;
  searchTerm?: string;
  itemId?: number;
  sortOption?: string;
}

export const getProducts = async (page = 1, perPage = 10, filters: FilterOptions = {}): Promise<{ data: ProductItem[]; count: number }> => {
  let query = supabase
    .from('products')
    .select('*', { count: 'exact' });

  // Apply filters
  if (filters.category && filters.category !== '_all') {
    query = query.eq('category', filters.category);
  }
  
  if (filters.brand && filters.brand !== '_all') {
    query = query.eq('brand', filters.brand);
  }
  
  if (filters.findSubcategory && filters.findSubcategory !== '_all') {
    query = query.eq('find_subcategory', filters.findSubcategory);
  }
  
  if (filters.minPrice !== undefined && !isNaN(filters.minPrice)) {
    query = query.gte('price', filters.minPrice);
  }
  
  if (filters.maxPrice !== undefined && !isNaN(filters.maxPrice)) {
    query = query.lte('price', filters.maxPrice);
  }
  
  if (filters.itemId !== undefined && !isNaN(filters.itemId)) {
    query = query.eq('_id', filters.itemId);
  }
  
  if (filters.searchTerm) {
    query = query.or(`name.ilike.%${filters.searchTerm}%,brand.ilike.%${filters.searchTerm}%,category.ilike.%${filters.searchTerm}%`);
  }

  // Apply sorting
  if (filters.sortOption) {
    const [field, direction] = filters.sortOption.split('_');
    if (field === 'id') {
      query = query.order('_id', { ascending: direction === 'asc' });
    } else {
      query = query.order(field, { ascending: direction === 'asc' });
    }
  } else {
    query = query.order('created_at', { ascending: false });
  }

  // Apply pagination
  const start = (page - 1) * perPage;
  query = query.range(start, start + perPage - 1);

  const { data, error, count } = await query;

  if (error) throw error;

  const products = (data || []) as unknown as ProductItem[];

  // Attach wishlist counts (derived from wishlist table)
  try {
    const ids = products.map((p: any) => Number(p._id)).filter((x) => !isNaN(x));
    if (ids.length > 0) {
      const { data: wishRows, error: wishErr } = await supabase
        .from('wishlist')
        .select('item_id')
        .in('item_id', ids);

      if (!wishErr) {
        const counts = new Map<number, number>();
        (wishRows || []).forEach((r: any) => {
          const itemId = r?.item_id;
          if (typeof itemId === 'number') {
            counts.set(itemId, (counts.get(itemId) || 0) + 1);
          }
        });

        products.forEach((p: any) => {
          p.wishlist_count = counts.get(Number(p._id)) || 0;
        });
      }
    }
  } catch {
    // If wishlist table/policies aren't available, just skip wishlist counts.
  }

  return { 
    data: products,
    count: count || 0 
  };
};

export const getProduct = async (id: number): Promise<ProductItem> => {
  const { data, error } = await supabase
    .from('products')
    .select('*')
    .eq('_id', id)
    .single();

  if (error) throw error;
  return data as unknown as ProductItem;
};

export const createProduct = async (formData: ProductItemFormData): Promise<ProductItem> => {
  let imageUrl = '';
  
  if (formData.image instanceof File) {
    try {
      imageUrl = await uploadFile(formData.image);
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  } else if (typeof formData.image === 'string') {
    // Proxy the image URL if it's a string
    imageUrl = await getProxiedImageUrl(formData.image);
  }

  const { data: { user } } = await supabase.auth.getUser();
  const timestamp = new Date().toISOString();

  // Find the highest existing ID to ensure sequential IDs
  const { data: maxIdData, error: maxIdError } = await supabase
    .from('products')
    .select('_id')
    .order('_id', { ascending: false })
    .limit(1);

  if (maxIdError) throw maxIdError;
  
  // Use the next available ID (highest + 1), or start at 1 if no items exist
  const nextId = maxIdData && maxIdData.length > 0 ? (maxIdData[0]._id as number) + 1 : 1;

  const insertData = {
    _id: nextId,
    name: formData.name,
    brand: formData.brand,
    price: formData.price,
    image: imageUrl,
    link: formData.link,
    category: formData.category,
    batch: formData.batch,
    top: formData.top || false,
    // Optional on the form; falls back to 'None' when missing/unsupported so
    // existing create flows stay backward-compatible.
    find_subcategory: normalizeFindSubcategory((formData as any).find_subcategory),
    created_by: user?.email || 'Unknown user',
    created_at: timestamp,
    updated_at: timestamp
  } as any;

  const { data, error } = await supabase
    .from('products')
    .insert(insertData)
    .select()
    .single();

  if (error) throw error;
  return data as unknown as ProductItem;
};

export const updateProduct = async (id: number, formData: ProductItemFormData): Promise<ProductItem> => {
  let imageUrl = '';
  
  if (formData.image instanceof File) {
    try {
      imageUrl = await uploadFile(formData.image);
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  } else if (typeof formData.image === 'string') {
    // Only proxy external images that aren't already from supabase
    if (!formData.image.includes('supabase.co')) {
      imageUrl = await getProxiedImageUrl(formData.image);
    } else {
      imageUrl = formData.image;
    }
  }

  const timestamp = new Date().toISOString();

  const updateData = {
    name: formData.name,
    brand: formData.brand,
    price: formData.price,
    image: imageUrl,
    link: formData.link,
    category: formData.category,
    batch: formData.batch,
    top: formData.top || false,
    // Only overwrite when the caller actually supplied a value; otherwise keep
    // the existing subcategory untouched (backward-compatible updates).
    ...((formData as any).find_subcategory !== undefined
      ? { find_subcategory: normalizeFindSubcategory((formData as any).find_subcategory) }
      : {}),
    updated_at: timestamp
  } as any;

  const { data, error } = await supabase
    .from('products')
    .update(updateData)
    .eq('_id', id)
    .select()
    .single();

  if (error) throw error;
  return data as unknown as ProductItem;
};

export const deleteProduct = async (id: number): Promise<void> => {
  const { error } = await supabase
    .from('products')
    .delete()
    .eq('_id', id);

  if (error) throw error;
};

/** PostgREST URLs get long fast — fetch/update IDs in chunks of 100. */
const ID_CHUNK_SIZE = 100;

const chunkIds = (ids: number[]): number[][] => {
  const chunks: number[][] = [];
  for (let i = 0; i < ids.length; i += ID_CHUNK_SIZE) {
    chunks.push(ids.slice(i, i + ID_CHUNK_SIZE));
  }
  return chunks;
};

/** Fetches specific products by their `_id` (bulk edit preview / validation). */
export const getProductsByIds = async (ids: number[]): Promise<ProductItem[]> => {
  const cleanIds = Array.from(new Set(ids.filter((id) => Number.isSafeInteger(id) && id > 0)));
  if (cleanIds.length === 0) return [];

  const results: ProductItem[] = [];
  for (const chunk of chunkIds(cleanIds)) {
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .in('_id', chunk);

    if (error) throw error;
    results.push(...((data || []) as unknown as ProductItem[]));
  }
  return results;
};

export interface BulkProductUpdates {
  category?: string;
  top?: boolean;
  findSubcategory?: string;
}

/**
 * Applies the same category/visibility changes to many products at once.
 * Returns the IDs that were actually updated.
 */
export const updateProductsBulk = async (
  ids: number[],
  updates: BulkProductUpdates,
): Promise<number[]> => {
  const cleanIds = Array.from(new Set(ids.filter((id) => Number.isSafeInteger(id) && id > 0)));
  if (cleanIds.length === 0) return [];

  const payload: {
    category?: string;
    top?: boolean;
    find_subcategory?: string;
    updated_at: string;
  } = {
    updated_at: new Date().toISOString(),
  };
  if (updates.category !== undefined) payload.category = updates.category;
  if (updates.top !== undefined) payload.top = updates.top;
  if (updates.findSubcategory !== undefined) {
    payload.find_subcategory = normalizeFindSubcategory(updates.findSubcategory);
  }

  const updatedIds: number[] = [];
  for (const chunk of chunkIds(cleanIds)) {
    const { data, error } = await supabase
      .from('products')
      .update(payload)
      .in('_id', chunk)
      .select('_id');

    if (error) throw error;
    updatedIds.push(...((data || []) as unknown as { _id: number }[]).map((row) => Number(row._id)));
  }
  return updatedIds;
};

// Export functions from the old services for backward compatibility
export const getClothingItems = getProducts;
export const getClothingItem = getProduct;
export const createClothingItem = createProduct;
export const updateClothingItem = updateProduct;
export const deleteClothingItem = deleteProduct;

export const getShoeItems = getProducts;
export const getShoeItem = getProduct;
export const createShoeItem = createProduct;
export const updateShoeItem = updateProduct;
export const deleteShoeItem = deleteProduct;
